import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  Paper,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface PerformanceInfoProps {
  data: {
    dhw: {
      capacity: string;
      cop: string;
      ambient: string;
      outlet: string;
      inlet: string;
    };
    hhwHighTemp: {
      capacity: string;
      cop: string;
      ambient: string;
      outlet: string;
    };
    cooling: {
      capacity: string;
      cop: string;
      ambient: string;
      outlet: string;
    };
  };
  onChange: (subSection: string, field: string, value: string) => void;
}

const PerformanceInfoSection: React.FC<PerformanceInfoProps> = ({ data, onChange }) => {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">2. Performance Information</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" paragraph>
            Prefilled temperature values are preferred values, along with allowable ranges. If the heat pump cannot operate at the prefilled conditions (eg: 7 °C ambient for DHW) but does fit within the desired range (eg: R-134a heat pump that can operate at 20 °C ambient and produce 65 °C outlet), replace the prefilled value. If it cannot operate within the specified range, leave the section (eg: DHW) blank.
          </Typography>
        </Box>

        {/* DHW Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>DHW</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Capacity"
                value={data.dhw.capacity}
                onChange={(e) => onChange('dhw', 'capacity', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="Ambient 7 °C, outlet 65 °C [kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="COP"
                value={data.dhw.cop}
                onChange={(e) => onChange('dhw', 'cop', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kW/kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ambient"
                value={data.dhw.ambient}
                onChange={(e) => onChange('dhw', 'ambient', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="7 °C (<25 °C)"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Outlet"
                value={data.dhw.outlet}
                onChange={(e) => onChange('dhw', 'outlet', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="65 °C (>59 °C)"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Inlet (if applicable)"
                value={data.dhw.inlet}
                onChange={(e) => onChange('dhw', 'inlet', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="15 °C"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* HHW/High Temp Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>HHW/High Temp</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Capacity"
                value={data.hhwHighTemp.capacity}
                onChange={(e) => onChange('hhwHighTemp', 'capacity', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="COP"
                value={data.hhwHighTemp.cop}
                onChange={(e) => onChange('hhwHighTemp', 'cop', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kW/kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ambient"
                value={data.hhwHighTemp.ambient}
                onChange={(e) => onChange('hhwHighTemp', 'ambient', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="7 °C (<25 °C)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Outlet"
                value={data.hhwHighTemp.outlet}
                onChange={(e) => onChange('hhwHighTemp', 'outlet', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="45/75 °C (>30 °C)"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Cooling Section */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Cooling</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Capacity"
                value={data.cooling.capacity}
                onChange={(e) => onChange('cooling', 'capacity', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="COP"
                value={data.cooling.cop}
                onChange={(e) => onChange('cooling', 'cop', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="[kW/kW]"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ambient"
                value={data.cooling.ambient}
                onChange={(e) => onChange('cooling', 'ambient', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="35 °C (>25 °C)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Outlet"
                value={data.cooling.outlet}
                onChange={(e) => onChange('cooling', 'outlet', e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                helperText="7 °C (<15 °C)"
              />
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

export default PerformanceInfoSection;